<template>
  <div class="ccma-banking">
    <!-- Full-screen loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
        <p class="loading-text">{{ t('consultantCorner.banking.LoadingBankingDetails') }}</p>
      </div>
    </div>

    <div v-if="showError" class="ccma-banking-error">
      <h2>{{ t('consultantCorner.banking.PageLoadError') }}</h2>
    </div>

    <!-- Error notification for save operations -->
    <div v-if="saveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('consultantCorner.banking.SaveError') }}</h3>
          <p>{{ saveErrorMessage }}</p>
        </div>
        <button class="error-close" @click="clearSaveError">×</button>
      </div>
      <div class="error-actions">
        <button class="baseBtn primaryBtn" @click="saveBankingInfo">{{ t('consultantCorner.banking.TryAgain') }}</button>
      </div>
    </div>

    <!-- Success notification -->
    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.banking.SaveSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>

    <div v-if="!showError && !isLoading">
      <!-- Header Section -->
      <div class="banking-header">
        <div class="first">
          <div class="banking-title">{{ t('consultantCorner.banking.BankingDetails') }}</div>
          <div class="banking-subtitle mb-1">{{ t('consultantCorner.banking.ManagePaymentAccounts') }}</div>
        </div>
      </div>

      <!-- Payout Accounts Section -->
      <div class="payout-accounts-section">
        <div class="payout-header">
          <h4>{{ t('consultantCorner.banking.ManagePayouts') }}</h4>
          <div class="payout-actions">
            <button
              v-if="payoutAccounts.length === 0"
              class="baseBtn primaryBtn"
              @click="openAddAccountModal"
            >
              {{ t('consultantCorner.banking.AddAccount') }}
            </button>
          </div>
        </div>

        <div v-if="payoutAccounts.length > 0" class="payout-grid">
          <div class="grid-header">
            <div class="grid-cell">{{ t('consultantCorner.banking.PayoutType') }}</div>
            <div class="grid-cell">{{ t('consultantCorner.banking.Country') }}</div>
            <div class="grid-cell">{{ t('consultantCorner.banking.BankName') }}</div>
            <div class="grid-cell">{{ t('consultantCorner.banking.AccountNumber') }}</div>
            <div class="grid-cell">{{ t('consultantCorner.banking.DeleteAccount') }}</div>
          </div>
          <div v-for="account in payoutAccounts" :key="account.bankingId" class="grid-row">
            <div class="grid-cell" :data-label="t('consultantCorner.banking.PayoutType')">{{ account.payPayoutSystemId }}</div>
            <div class="grid-cell" :data-label="t('consultantCorner.banking.Country')">{{ getCountryFromCode(account.countryCode) }}</div>
            <div class="grid-cell" :data-label="t('consultantCorner.banking.BankName')">{{ account.bankName }}</div>
            <div class="grid-cell" :data-label="t('consultantCorner.banking.AccountNumber')">{{ maskedAccountNumber(account.accountNumber) }}</div>
            <div class="grid-cell" :data-label="t('consultantCorner.banking.DeleteAccount')">
              <button
                class="delete-btn"
                @click="openDeleteConfirmation(account)"
                :title="t('consultantCorner.banking.DeleteAccount')"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div v-else class="no-accounts">
          <p>{{ t('consultantCorner.banking.NoPayoutAccounts') }}</p>
        </div>
      </div>

      <!-- Add Account Modal -->
      <div v-if="showAddAccountModal" class="modal-overlay" @click="closeAddAccountModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ t('consultantCorner.banking.AddNewAccount') }}</h3>
            <button class="modal-close" @click="closeAddAccountModal">×</button>
          </div>
          <div class="modal-body">
            <div class="form-field">
              <label class="form-label required">{{ t('consultantCorner.banking.BICCode') }}</label>
              <input
                v-model="modalFormData.bic"
                type="text"
                class="form-input"
                :placeholder="t('consultantCorner.banking.BICPlaceholder')"
                maxlength="11"
                @input="onModalFieldChange"
                @blur="onModalBicChange"
              />
              <div v-if="modalErrors.bic" class="field-error">{{ modalErrors.bic }}</div>
              <div v-if="showModalInvalidBicAlert" class="field-error">{{ t('consultantCorner.banking.InvalidBICCode') }}</div>
            </div>
            <div class="form-field">
              <label class="form-label">{{ t('consultantCorner.banking.BankName') }}</label>
              <input
                v-model="modalFormData.bankName"
                type="text"
                class="form-input"
                :placeholder="t('consultantCorner.banking.BankNamePlaceholder2')"
                maxlength="100"
                readonly
              />
              <div v-if="modalErrors.bankName" class="field-error">{{ modalErrors.bankName }}</div>
            </div>
            <div class="form-field">
              <label class="form-label required">{{ t('consultantCorner.banking.IBAN') }}</label>
              <input
                v-model="modalFormData.iban"
                type="text"
                class="form-input"
                :placeholder="t('consultantCorner.banking.IBANPlaceholder')"
                maxlength="34"
                @input="onModalFieldChange"
              />
              <div v-if="modalErrors.iban" class="field-error">{{ modalErrors.iban }}</div>
            </div>
            <div class="form-field">
              <label class="form-label required">{{ t('consultantCorner.banking.AccountType') }}</label>
              <select
                v-model="modalFormData.accountType"
                class="form-input"
                @change="onModalFieldChange"
              >
                <option value="SAV">{{ t('consultantCorner.banking.DirectDeposit') }}</option>
                <option value="CHK">{{ t('consultantCorner.banking.Check') }}</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button class="baseBtn secondaryBtn" @click="closeAddAccountModal">
              {{ t('consultantCorner.banking.Cancel') }}
            </button>
            <button
              class="baseBtn primaryBtn"
              @click="saveNewAccount"
              :disabled="isModalSaving || !isModalFormValid"
            >
              <span v-if="isModalSaving">{{ t('consultantCorner.banking.Saving') }}...</span>
              <span v-else>{{ t('consultantCorner.banking.Save') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ t('consultantCorner.banking.DeleteAccount') }}</h3>
            <button class="modal-close" @click="closeDeleteModal">×</button>
          </div>
          <div class="modal-body">
            <p>{{ t('consultantCorner.banking.DeleteAccountConfirmation') }}</p>
            <p class="warning-text">{{ t('consultantCorner.banking.DeleteAccountWarning') }}</p>
          </div>
          <div class="modal-footer">
            <button class="baseBtn secondaryBtn" @click="closeDeleteModal">
              {{ t('consultantCorner.banking.Cancel') }}
            </button>
            <button
              class="baseBtn dangerBtn"
              @click="confirmDeleteAccount"
              :disabled="isDeleting"
            >
              <span v-if="isDeleting">{{ t('consultantCorner.banking.Removing') }}...</span>
              <span v-else>{{ t('consultantCorner.banking.Delete') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Banking Information Form -->
      <div class="banking-form-section">
        <div class="form-section-header">
          <h4>{{ t('consultantCorner.banking.ConsultantPaymentAccount') }}</h4>
        </div>
        <div class="banking-form">
          <div class="form-row">
            <div class="form-field">
              <label class="form-label required">{{ t('consultantCorner.banking.BICCode') }}</label>
              <input
                v-model="formData.bic"
                type="text"
                class="form-input"
                :placeholder="currentBankingInfo?.routingNumber || t('consultantCorner.banking.BICPlaceholder')"
                maxlength="11"
                @input="validateForm"
                @blur="onBicChange"
              />
              <div v-if="errors.bic" class="field-error">{{ errors.bic }}</div>
              <div v-if="showInvalidBicAlert" class="field-error">{{ t('consultantCorner.banking.InvalidBICCode') }}</div>
            </div>

            <div class="form-field">
              <label class="form-label">{{ t('consultantCorner.banking.BankName') }}</label>
              <input
                v-model="formData.bankName"
                type="text"
                class="form-input"
                :placeholder="currentBankingInfo?.bankName || t('consultantCorner.banking.BankNamePlaceholder2')"
                maxlength="100"
                readonly
              />
              <div v-if="errors.bankName" class="field-error">{{ errors.bankName }}</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="form-label required">{{ t('consultantCorner.banking.AccountHolder') }}</label>
              <input
                v-model="formData.accountHolder"
                type="text"
                class="form-input"
                :placeholder="currentBankingInfo?.accountHolder || t('consultantCorner.banking.AccountHolderPlaceholder')"
                maxlength="100"
                @input="validateForm"
              />
              <div v-if="errors.accountHolder" class="field-error">{{ errors.accountHolder }}</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="form-label">{{ t('consultantCorner.banking.CurrentIBAN') }}</label>
              <input
                type="text"
                class="form-input"
                :value="maskedIBAN(currentBankingInfo?.iban || '')"
                readonly
                disabled
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="form-label required">{{ t('consultantCorner.banking.NewIBAN') }}</label>
              <input
                v-model="formData.iban"
                type="text"
                class="form-input"
                :placeholder="t('consultantCorner.banking.IBANPlaceholder')"
                maxlength="34"
                @input="validateForm"
              />
              <div v-if="errors.iban" class="field-error">{{ errors.iban }}</div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="form-actions">
            <button
              class="baseBtn primaryBtn"
              :disabled="isSaving || !isFormValid"
              @click="saveBankingInfo"
            >
              <span v-if="isSaving">{{ t('consultantCorner.banking.Saving') }}...</span>
              <span v-else>{{ t('consultantCorner.banking.SaveNewAccount') }}</span>
            </button>

            <button
              v-if="currentBankingInfo"
              class="baseBtn dangerBtn"
              @click="removePaymentAccount"
              :disabled="isRemoving"
            >
              <span v-if="isRemoving">{{ t('consultantCorner.banking.Removing') }}...</span>
              <span v-else>{{ t('consultantCorner.banking.RemoveAccount') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Loader } from '@progress/kendo-vue-indicators'
import { PwsBeeBankingGetList, BeeBankingCreate, PwsBeeBankingDelete, BankRoutingGetList, type IPwsBeeBankingResponse } from '@/api/Payments'
import { BeeVirtualCheckGetListConnector, BeeVirtualCheckCreateConnector, BeeVirtualCheckUpdateConnector, BeeVirtualCheckDeleteConnector } from '@/api/VirtualCheck'
import { GetBeeNumberAuthenicatedUser } from '@/stores/SettingsStore'
import { getCountryName } from '@/config/countries'

const { t } = useI18n()

// Reactive state
const isLoading = ref(false)
const isSaving = ref(false)
const isRemoving = ref(false)
const isDeleting = ref(false)
const showError = ref(false)
const saveError = ref(false)
const saveSuccess = ref(false)
const saveErrorMessage = ref('')
const showInvalidBicAlert = ref(false)
const showModalInvalidBicAlert = ref(false)

// Define type for banking item
type BankingItem = IPwsBeeBankingResponse['items'][0]

// Current banking information (OPA account from VirtualCheck)
const currentBankingInfo = ref<BankingItem | null>(null)
const beeVirtualCheckGuid = ref<string | null>(null)

// Payout accounts data
const payoutAccounts = ref<BankingItem[]>([])

// Delete modal state
const showDeleteModal = ref(false)
const accountToDelete = ref<BankingItem | null>(null)

// Add Account Modal state
const showAddAccountModal = ref(false)
const isModalSaving = ref(false)

// Modal form data
const modalFormData = reactive({
  bic: '',
  bankName: '',
  iban: '',
  accountType: 'SAV'
})

// Modal form validation errors
const modalErrors = reactive({
  bic: '',
  bankName: '',
  iban: ''
})

// Form data
const formData = reactive({
  bic: '',
  bankName: '',
  accountHolder: '',
  iban: ''
})

// Form validation errors
const errors = reactive({
  bic: '',
  bankName: '',
  accountHolder: '',
  iban: ''
})

// Computed properties
const isFormValid = computed(() => {
  return formData.bic.trim() !== '' &&
         formData.bankName.trim() !== '' &&
         formData.accountHolder.trim() !== '' &&
         formData.iban.trim() !== '' &&
         !errors.bic &&
         !errors.bankName &&
         !errors.accountHolder &&
         !errors.iban
})

const isModalFormValid = computed(() => {
  return modalFormData.bic.trim() !== '' &&
         modalFormData.bankName.trim() !== '' &&
         modalFormData.iban.trim() !== '' &&
         !modalErrors.bic &&
         !modalErrors.bankName &&
         !modalErrors.iban
})

const mandateReference = computed(() => {
  const timestamp = Date.now()
  return `SEPA-${timestamp.toString().slice(-8)}`
})

// Utility functions
const maskedIBAN = (iban: string): string => {
  if (!iban || iban.length < 4) return iban
  const lastFour = iban.slice(-4)
  const masked = '*'.repeat(iban.length - 4)
  return masked + lastFour
}

const maskedAccountNumber = (accountNumber?: string): string => {
  if (!accountNumber) return ''
  const lastFour = accountNumber.slice(-4)
  return `************${lastFour}`
}

const getCountryFromCode = (countryCode?: string): string => {
  if (!countryCode) return ''
  return getCountryName(countryCode, (key: string) => t(key))
}

// Validation functions
const validateBIC = (bic: string): string => {
  if (!bic.trim()) return t('consultantCorner.banking.BICRequired')

  return ''
}

const validateIBAN = (iban: string): string => {
  if (!iban.trim()) return t('consultantCorner.banking.IBANRequired')
  const cleanIban = iban.replace(/\s/g, '').toUpperCase()
  if (cleanIban.length < 15 || cleanIban.length > 34) {
    return t('consultantCorner.banking.IBANInvalidLength')
  }
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleanIban)) {
    return t('consultantCorner.banking.IBANInvalidFormat')
  }
  return ''
}

const validateForm = () => {
  errors.bic = validateBIC(formData.bic)
  errors.iban = validateIBAN(formData.iban)
}

const validateFormComplete = () => {
  errors.bic = validateBIC(formData.bic)
  errors.bankName = formData.bankName.trim() ? '' : t('consultantCorner.banking.BankNameRequired')
  errors.accountHolder = formData.accountHolder.trim() ? '' : t('consultantCorner.banking.AccountHolderRequired')
  errors.iban = validateIBAN(formData.iban)
}

// BIC change handler - lookup bank name from BIC code
const onBicChange = async () => {
  try {
    const bic = formData.bic.trim().toUpperCase()

    if (!bic) {
      showInvalidBicAlert.value = false
      return
    }

    const bicError = validateBIC(bic)
    if (bicError) {
      formData.bankName = ''
      showInvalidBicAlert.value = false
      return
    }

    const filter = {
      ResponseFilter: {
        Where: {
          OperatorType: 'And',
          Operands: [
            {
              Field: 'validationValue',
              Value: bic,
              FilterType: 'Equal',
            },
          ],
        },
      },
    }

    const response = await BankRoutingGetList(filter)

    if (response && response.items && response.items.length > 0) {
      const bankInfo = response.items[0]
      formData.bankName = bankInfo.bankName || ''
      errors.bankName = ''
      showInvalidBicAlert.value = false
    } else {
      formData.bankName = ''
      showInvalidBicAlert.value = true
    }
  } catch (error: any) {
    console.error('Error fetching bank routing information:', error)
    formData.bankName = ''
    showInvalidBicAlert.value = true
  }
}

// Modal methods
const openAddAccountModal = () => {
  showAddAccountModal.value = true
  modalFormData.bic = ''
  modalFormData.bankName = ''
  modalFormData.iban = ''
  modalFormData.accountType = 'SAV'
  clearModalErrors()
}

const closeAddAccountModal = () => {
  showAddAccountModal.value = false
}

const clearModalErrors = () => {
  modalErrors.bic = ''
  modalErrors.bankName = ''
  modalErrors.iban = ''
}

const onModalFieldChange = () => {
  if (modalFormData.bic.trim()) {
    modalErrors.bic = validateBIC(modalFormData.bic)
  }
  if (modalFormData.bankName.trim()) {
    modalErrors.bankName = ''
  }
  if (modalFormData.iban.trim()) {
    modalErrors.iban = validateIBAN(modalFormData.iban)
  }
}

// Modal BIC change handler - lookup bank name from BIC code
const onModalBicChange = async () => {
  try {
    const bic = modalFormData.bic.trim().toUpperCase()

    if (!bic) {
      showModalInvalidBicAlert.value = false
      return
    }

    const bicError = validateBIC(bic)
    if (bicError) {
      modalFormData.bankName = ''
      showModalInvalidBicAlert.value = false
      return
    }

    const filter = {
      ResponseFilter: {
        Where: {
          OperatorType: 'And',
          Operands: [
            {
              Field: 'validationValue',
              Value: bic,
              FilterType: 'Equal',
            },
          ],
        },
      },
    }

    const response = await BankRoutingGetList(filter)

    if (response && response.items && response.items.length > 0) {
      const bankInfo = response.items[0]
      modalFormData.bankName = bankInfo.bankName || ''
      modalErrors.bankName = ''
      showModalInvalidBicAlert.value = false
    } else {
      modalFormData.bankName = ''
      showModalInvalidBicAlert.value = true
    }
  } catch (error: any) {
    console.error('Error fetching bank routing information for modal:', error)
    modalFormData.bankName = ''
    showModalInvalidBicAlert.value = true
  }
}

const validateModalForm = () => {
  clearModalErrors()
  if (!modalFormData.bic.trim()) {
    modalErrors.bic = t('consultantCorner.banking.BICRequired')
  } else if (validateBIC(modalFormData.bic)) {
    modalErrors.bic = validateBIC(modalFormData.bic)
  }
  if (!modalFormData.bankName.trim()) {
    modalErrors.bankName = t('consultantCorner.banking.BankNameRequired')
  }
  if (!modalFormData.iban.trim()) {
    modalErrors.iban = t('consultantCorner.banking.IBANRequired')
  } else if (validateIBAN(modalFormData.iban)) {
    modalErrors.iban = validateIBAN(modalFormData.iban)
  }
}

const saveNewAccount = async () => {
  validateModalForm()
  if (!isModalFormValid.value) return

  try {
    isModalSaving.value = true
    saveError.value = false

    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (beeNumber) {
      const cleanIban = modalFormData.iban.replace(/\s/g, '').toUpperCase()

      await BeeBankingCreate(beeNumber, {
        bankAccountType: 'IBAN',
        bankName: modalFormData.bankName,
        bic: modalFormData.bic.toUpperCase(),
        iban: cleanIban,
        directDeposit: true,
        opa: false,
        payoutPercentage: 100,
        cddCodeIdBankingType: 'PAYOUT',
        cddCodeIdAccountType: modalFormData.accountType
      })

      saveSuccess.value = true
      setTimeout(() => {
        saveSuccess.value = false
      }, 5000)

      await loadBankingInfo()
      closeAddAccountModal()
    }
  } catch (error: any) {
    console.error('Error saving new account:', error)
    saveError.value = true
    saveErrorMessage.value = error instanceof Error ? error.message : t('consultantCorner.banking.UnknownError')
  } finally {
    isModalSaving.value = false
  }
}

// API functions
const loadBankingInfo = async () => {
  try {
    isLoading.value = true
    showError.value = false
    payoutAccounts.value = []

    // Get beeNumber from user context
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (beeNumber) {
      // Load OPA account from BeeVirtualCheckGetListConnector
      const virtualCheckResponse = await BeeVirtualCheckGetListConnector()
      if (virtualCheckResponse.items && virtualCheckResponse.items.length > 0) {
        const firstVirtualCheck = virtualCheckResponse.items[0]

        // Store the BeeVirtualCheckGuid for updates
        beeVirtualCheckGuid.value = firstVirtualCheck.beeVirtualCheckGuid

        // Map VirtualCheck fields to BankingItem format for display
        currentBankingInfo.value = {
          bankingId: 0, // Not used for VirtualCheck
          payoutConfigId: 0, // Not used for VirtualCheck
          routingNumber: firstVirtualCheck.abaNumber, // BIC
          accountHolder: firstVirtualCheck.accountHolder, // Account Holder
          accountNumber: firstVirtualCheck.accountNumber, // IBAN
          iban: firstVirtualCheck.accountNumber, // IBAN
          bankName: firstVirtualCheck.bankName, // Bank Name
          payoutPercentage: 0,
          codeIdAccountType: firstVirtualCheck.codeIdAccountType,
          createdDate: firstVirtualCheck.createdDate || '',
          beeNumber: {
            beeNumber: firstVirtualCheck.beeNumber.beeNumber,
            externalIds: [],
            salesOrganizationId: firstVirtualCheck.beeNumber.salesOrganizationId
          },
          links: []
        } as BankingItem
      } else {
        currentBankingInfo.value = null
        beeVirtualCheckGuid.value = null
      }

      // Load payout accounts from BeeBanking (all items are payout accounts)
      const response: IPwsBeeBankingResponse = await PwsBeeBankingGetList({ beeNumber })
      if (response.items && response.items.length > 0) {
        payoutAccounts.value = response.items
      } else {
        payoutAccounts.value = []
      }
    } else {
      payoutAccounts.value = []
    }

  } catch (error) {
    console.error('Error loading banking info:', error)
    showError.value = true
  } finally {
    isLoading.value = false
  }
}

const saveBankingInfo = async () => {
  validateFormComplete()
  if (!isFormValid.value) return

  try {
    isSaving.value = true
    saveError.value = false

    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (beeNumber) {
      const baseRequest = {
        BeeNumber: beeNumber,
        CddCodeIdAccountType: currentBankingInfo.value?.codeIdAccountType?.codeId || 'CHK',
        BankName: formData.bankName,
        AccountHolder: formData.accountHolder,
        AbaNumber: formData.bic.toUpperCase(),
        AccountNumber: formData.iban.replace(/\s/g, '').toUpperCase(),
        BeeActivated: 0,
        BeeActivatedDate: null,
        IsEnabled: 0,
        IsVerified: 0
      }

      if (beeVirtualCheckGuid.value) {
        const updateRequest = {
          BeeVirtualCheckGuid: beeVirtualCheckGuid.value,
          ...baseRequest
        }
        await BeeVirtualCheckUpdateConnector(updateRequest)
      } else {
        await BeeVirtualCheckCreateConnector(baseRequest)
      }

      saveSuccess.value = true
      setTimeout(() => {
        saveSuccess.value = false
      }, 5000)

      // Reload banking info to show updated data
      await loadBankingInfo()

      // Clear form
      formData.bic = ''
      formData.bankName = ''
      formData.accountHolder = ''
      formData.iban = ''
    }
  } catch (error: any) {
    console.error('Error saving banking info:', error)
    saveError.value = true
    saveErrorMessage.value = error instanceof Error ? error.message : t('consultantCorner.banking.UnknownError')
  } finally {
    isSaving.value = false
  }
}

const removePaymentAccount = async () => {
  if (!beeVirtualCheckGuid.value) return

  try {
    isRemoving.value = true
    saveError.value = false

    const beeNumber = await GetBeeNumberAuthenicatedUser()
    // Delete OPA account using BeeVirtualCheckDeleteConnector
    await BeeVirtualCheckDeleteConnector({
      BeeNumber: beeNumber,
      BeeVirtualCheckGuid: beeVirtualCheckGuid.value
    })

    saveSuccess.value = true
    setTimeout(() => {
      saveSuccess.value = false
    }, 5000)

    // Reload banking info to show updated data
    await loadBankingInfo()

  } catch (error: any) {
    console.error('Error removing payment account:', error)
    saveError.value = true
    saveErrorMessage.value = error instanceof Error ? error.message : t('consultantCorner.banking.UnknownError')
  } finally {
    isRemoving.value = false
  }
}

// Delete modal methods
const openDeleteConfirmation = (account: BankingItem) => {
  accountToDelete.value = account
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  accountToDelete.value = null
}

const confirmDeleteAccount = async () => {
  if (!accountToDelete.value) return

  try {
    isDeleting.value = true
    saveError.value = false

    const response = await PwsBeeBankingDelete(accountToDelete.value.bankingId)

    // Check for percentage validation error
    if (response?.responseStatus?.errorCode === 'BeePayoutConfigInvalidPayoutPercentException') {
      saveError.value = true
      saveErrorMessage.value = t('consultantCorner.banking.InvalidPayoutPercentage')
      // Close modal first
      closeDeleteModal()
      // Then reload to show current state
      await loadBankingInfo()
      return
    }

    // Close modal before reload
    closeDeleteModal()

    // Reload banking info to show updated data
    await loadBankingInfo()

    saveSuccess.value = true
    setTimeout(() => {
      saveSuccess.value = false
    }, 5000)

  } catch (error: any) {
    console.error('Error deleting payout account:', error)
    saveError.value = true

    // Check for percentage validation error in catch block
    if (error?.responseStatus?.errorCode === 'BeePayoutConfigInvalidPayoutPercentException') {
      saveErrorMessage.value = t('consultantCorner.banking.InvalidPayoutPercentage')
    } else {
      saveErrorMessage.value = error instanceof Error ? error.message : t('consultantCorner.banking.DeleteError')
    }

    // Close modal and reload
    closeDeleteModal()
    await loadBankingInfo()
  } finally {
    isDeleting.value = false
  }
}

// Notification handlers
const clearSaveError = () => {
  saveError.value = false
  saveErrorMessage.value = ''
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}

// Lifecycle
onMounted(() => {
  loadBankingInfo()
})
</script>

<style lang="scss" scoped>
.ccma-banking {
  padding: $card-padding;
  max-width: 1200px;
  margin: 0 auto;
}

/* Loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(white, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  text-align: center;
}

.loading-text {
  @include typography-body;
  margin-top: 1rem;
  color: #666;
}

/* Error states */
.ccma-banking-error {
  text-align: center;
  padding: 2rem;
  color: #dc3545;
}

/* Notifications */
.save-error-notification, .save-success-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  max-width: 400px;
  z-index: $z-notification;
  border-radius: $radius-md;
  box-shadow: 0 4px 12px rgba(black, 0.2);
  animation: slideIn 0.3s ease-out;
}

.save-error-notification {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
}

.save-success-notification {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
}

.error-content, .success-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
}

.error-icon, .success-icon {
  @include typography-h3;
  margin-right: 1rem;
  margin-top: 0.25rem;
}

.error-details, .success-message {
  flex: 1;
}

.error-details h3 {
  @include typography-body;
  margin: 0 0 0.5rem 0;
  color: #721c24;
}

.error-details p, .success-message p {
  @include typography-small;
  margin: 0;
  color: #721c24;
}

.success-message p {
  color: #155724;
}

.error-close, .success-close {
  @include typography-h3;
  background: none;
  border: none;
  color: #721c24;
  cursor: pointer;
  padding: 0;
  margin-left: 1rem;
}

.success-close {
  color: #155724;
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  text-align: right;
}

/* Header */
.banking-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e67e22;
}

.banking-title {
  @include typography-h2;
  @include font-weight-semibold;
  color: #e67e22;
  margin-bottom: 0.5rem;
}

.banking-subtitle {
  @include typography-body;
  color: #666;
}

.banking-buttons {
  display: flex;
  gap: 0.75rem;
}

/* Payout accounts section */
.payout-accounts-section {
  margin-bottom: 2rem;
}

.payout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.payout-header h4 {
  @include typography-h4;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

/* Form Section Header */
.form-section-header {
  margin-bottom: 1.5rem;
}

.form-section-header h4 {
  @include typography-h4;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

.payout-actions {
  display: flex;
  gap: 0.75rem;
}

.payout-grid {
  background-color: var(--vt-c-white);
  border: 1px solid #dee2e6;
  border-radius: $radius-md;
  overflow: hidden;
}


.grid-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1.5fr 1.5fr 80px;
  border-bottom: 1px solid #dee2e6;
}

.grid-row:last-child {
  border-bottom: none;
}

.grid-cell {
  @include typography-small;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.grid-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1.5fr 1.5fr 80px;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #dee2e6;
}

.grid-header .grid-cell {
  @include font-weight-semibold;
  color: #495057;
  background-color: var(--vt-c-white-soft);
}

.delete-btn {
  width: 32px;
  height: 32px;
  border-radius: $radius-sm;
  margin: 0;
  background-color: #dc3545;
  @include typography-body-large;
  @include font-weight-bold;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease-in-out;
}

.delete-btn:hover {
  background-color: #c82333;
}

.delete-btn:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.warning-text {
  @include font-weight-semibold;
  color: #dc3545;
  margin-top: 0.5rem;
}

.no-accounts {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #dee2e6;
  border-radius: $radius-md;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(black, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $z-critical-backdrop;
}

.modal-content {
  background-color: white;
  border-radius: $radius-md;
  box-shadow: 0 4px 20px rgba(black, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  @include typography-h4;
  margin: 0;
  color: #e67e22;
}

.modal-close {
  @include typography-h3;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  color: #495057;
}

.modal-body {
  padding: 1.5rem;
}

.modal-body .form-field {
  margin-bottom: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
}

/* Responsive design for smaller screens */
@media (max-width: 768px) {
  .grid-header {
    display: none;
  }

  .grid-row {
    display: block;
    border: 1px solid #dee2e6;
    border-radius: $radius-md;
    margin-bottom: 1rem;
    padding: 1rem;
  }

  .grid-cell {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--vt-c-white-soft);
  }

  .grid-cell:last-child {
    border-bottom: none;
    justify-content: center;
    padding-top: 1rem;
  }

  .grid-cell::before {
    @include font-weight-semibold;
    content: attr(data-label) ": ";
    color: #495057;
  }

  .grid-cell:last-child::before {
    display: none;
  }

  .delete-btn {
    width: 40px;
    height: 40px;
  }
}

/* Banking form section */
.banking-form-section {
  margin-bottom: 2rem;
}

.banking-form {
  background-color: var(--vt-c-white);
  border: 1px solid #dee2e6;
  border-radius: $radius-md;
  padding: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-label {
  @include typography-small;
  @include font-weight-semibold;
  color: #495057;
  margin-bottom: 0.5rem;
  min-height: 2.5rem;
  display: flex;
  align-items: flex-end;
  line-height: 1.2;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-input:focus {
  outline: none;
  border-color: #e67e22;
  box-shadow: 0 0 0 0.2rem rgba(#e67e22, 0.25);
}

.form-input:disabled {
  background-color: var(--vt-c-white-soft);
  color: #6c757d;
  cursor: not-allowed;
}

.field-error {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

/* Form actions */
.form-actions {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
}

/* Buttons */
.baseBtn {
  padding: 0.75rem 1.5rem;
  @include typography-small;
  @include font-weight-semibold;
  border: none;
  border-radius: $button-radius;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}


.primaryBtn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.secondaryBtn {
  background-color: #6c757d;
  color: white;
}

.secondaryBtn:hover:not(:disabled) {
  background-color: #5a6268;
}

.secondaryBtn:disabled {
  background-color: #adb5bd;
  cursor: not-allowed;
}

.dangerBtn {
  background-color: #dc3545;
  color: white;
}

.dangerBtn:hover:not(:disabled) {
  background-color: #c82333;
}

.dangerBtn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* Animations */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive design */
@media (max-width: 767px) {
  .ccma-banking {
    padding: $spacing-2-5;
  }

  .banking-header {
    flex-direction: column;
    align-items: stretch;
  }

  .banking-buttons {
    margin-top: 1rem;
    justify-content: stretch;
  }

  .banking-buttons .baseBtn {
    flex: 1;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .payout-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .payout-actions {
    justify-content: stretch;
  }

  .payout-actions .baseBtn {
    flex: 1;
  }

  .grid-header, .grid-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .grid-cell {
    padding: 0.75rem;
    border-bottom: 1px solid #dee2e6;
  }

  .grid-header .grid-cell {
    display: none;
  }

  .grid-row .grid-cell:before {
    @include font-weight-semibold;
    content: attr(data-label);
    color: #495057;
    display: block;
    margin-bottom: 0.25rem;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer .baseBtn {
    width: 100%;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions .baseBtn {
    width: 100%;
  }

  .save-error-notification, .save-success-notification {
    position: fixed;
    top: 10px;
    left: 10px;
    right: 10px;
    max-width: none;
  }
}
</style>
